import { Component, OnInit, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild, ElementRef } from '@angular/core'
import { TreeNode, SelectItem } from 'primeng/api'
import { TreeTable } from 'primeng/treetable'
import { JmeleonActionsFacadeService } from '../../services/jmeleon-actions-facade.service'
import { Observable, of, Subject } from 'rxjs'
import { take, takeUntil, tap, filter, switchMap } from 'rxjs/operators'
import { TreeTableData, TreeTableEntityNode } from '../../models/actions-tree-node.model'

interface CheckboxEvent {
  checked: boolean
}
interface SectionSelectEvent {
  value: {
    key: string
    hasSelectedNodes: boolean
    selectedCount: number
  }
}

const DEFAULT_LAST_SECTIONS_COUNT = 5

@Component({
  selector: 'jml-lib-right-actions-editor',
  templateUrl: './right-actions-editor.component.html',
  styleUrls: ['./right-actions-editor.component.scss']
})
export class RightActionsEditorComponent implements OnInit, OnChanges, OnDestroy {
  private static readonly STANDARD_ACTIONS = [
    'create',
    'read',
    'write',
    'delete',
    'export',
    'upload',
    'download'
  ] as const

  @ViewChild('treeTable', { static: false }) treeTable: TreeTable
  @ViewChild('filterInput') filterInput: ElementRef
  @ViewChild('sectionsPanel', { static: false }) sectionsPanel: ElementRef
  @Input() ownerId: number
  @Input() stickyTableHeader: boolean = true
  public fixedPanelMinWidth: string | null = null
  public lastSectionsCount: number = DEFAULT_LAST_SECTIONS_COUNT
  public isInited: boolean = false

  treeTableData$: Observable<TreeTableData>
  currentSectionEntities$: Observable<TreeTableEntityNode[]>
  sections$: Observable<SelectItem[]>
  isLoading$: Observable<boolean>
  disableEditing$: Observable<boolean>

  selectedActions: TreeNode[]
  selectedSection: {
    key: string
    hasSelectedNodes: boolean
    selectedCount: number
  } | null = null
  allTreesExpanded: boolean = false

  actionColumns = [
    { field: 'entity', header: 'Entity/Field' },
    { field: 'create', header: '<i class="fa fa-plus"></i>', action: 'create', tooltip: 'Create' },
    { field: 'read', header: '<i class="fa fa-eye"></i>', action: 'read', tooltip: 'Read' },
    { field: 'write', header: '<i class="fa fa-edit"></i>', action: 'write', tooltip: 'Write' },
    { field: 'delete', header: '<i class="fa fa-trash"></i>', action: 'delete', tooltip: 'Delete' },
    { field: 'export', header: '<i class="fa fa-file-export"></i>', action: 'export', tooltip: 'Export' },
    { field: 'upload', header: '<i class="fa fa-upload"></i>', action: 'upload', tooltip: 'Upload' },
    { field: 'download', header: '<i class="fa fa-download"></i>', action: 'download', tooltip: 'Download' }
  ]

  filterValue: string = ''
  filteredEntities: TreeTableEntityNode[] = []

  private allEntities: TreeTableEntityNode[] = []
  private destroy$ = new Subject<void>()

  constructor(private facade: JmeleonActionsFacadeService) {}

  /**
   * Initializes the component by setting up observables for tree table data, sections, loading state, and editing permissions.
   * Automatically selects the first available section if no section is currently selected or the current section is no longer available.
   * Manages section selection through the facade service and handles component initialization lifecycle.
   */
  ngOnInit() {
    this.treeTableData$ = this.facade.treeTableData$
    this.sections$ = this.facade.sections$
    this.isLoading$ = this.facade.isLoading$
    this.disableEditing$ = this.facade.disableEditing$
    this.tryToMakeRequestActionTreeFromBackend()
    this.actionsWithSectionOnInit()
    this.subscribeToLoadingState()
  }

  /**
   * Lifecycle hook that responds to changes in the component's input properties.
   * When the ownerId changes, it keeps current display state and overlays skeleton during loading.
   *
   * @param changes SimpleChanges object containing current and previous property values
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.ownerId) {
      if (this.selectedSection?.key) {
        this.facade.setLastSelectedSectionKey(this.selectedSection.key)
      }

      // ✅ Fix panel width before loading starts
      this.fixPanelWidthBeforeLoading()

      this.facade.resetState()

      if (this.ownerId) {
        this.facade.requestActionTreeFromBackend(this.ownerId)
      }
    }
  }

  /**
   * Lifecycle hook that cleans up the component by completing the destruction subject.
   * This method ensures that all observables using the `destroy$` subject are properly unsubscribed,
   * preventing potential memory leaks and unnecessary background processing.
   */
  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  /**
   * Handles toggling an action for a specific entity in the tree table.
   * Updates the local entity state and communicates the action toggle to the facade service.
   *
   * @param entity The tree table entity node being modified
   * @param action The specific action being toggled
   * @param event The checkbox event triggering the action toggle
   */
  onActionToggle(entity: TreeTableEntityNode, action: string, event: CheckboxEvent): void {
    // Update local state immediately
    entity.actions[action] = event.checked

    const entityPath = this.buildEntityPathForAction(entity, action)

    this.facade.toggleTreeTableAction(this.selectedSection?.key || '', entityPath, action, event.checked)
  }

  /**
   * Handles toggling the selection state of an entity in the tree table.
   *
   * This method manages entity selection with special handling for:
   * - Root-level dynamic actions
   * - Recursive selection/deselection of child entities
   *
   * @param entity The tree table entity node being toggled
   * @param event The checkbox event triggering the entity selection change
   */
  onEntityToggle(entity: TreeTableEntityNode, event: CheckboxEvent): void {
    if (this.isRootLevelDynamicAction(entity)) {
      // Update local state
      entity.selected = event.checked

      // Send backend request for the action
      const entityPath = this.buildEntityPathForAction(entity, entity.name)
      this.facade.toggleTreeTableAction(this.selectedSection?.key || '', entityPath, entity.name, event.checked)

      return
    }

    if (event.checked) {
      this.selectEntityAndChildren(entity)
    } else {
      this.unselectEntityAndChildren(entity)
    }
  }

  /**
   * Handles the selection of a new section in the permissions tree table.
   *
   * This method performs the following actions when a section is selected:
   * - Updates the selected section via the facade
   * - Refreshes the entities for the newly selected section
   * - Applies any existing filter to the new section
   *
   * @param event The event containing the newly selected section details
   */
  sectionSelected(event: SectionSelectEvent): void {
    const newSectionKey = event.value.key
    this.facade.setLastSelectedSectionKey(newSectionKey)
    this.facade.selectSection(newSectionKey)

    this.updateActionColumnsHeader(event.value.key)

    this.updateEntitiesForCurrentSection()

    this.clearFilter()
  }

  /**
   * Toggles the expanded/collapsed state of all tree nodes in the tree table.
   *
   * This method programmatically clicks all tree node togglers to expand or collapse
   * the entire tree table, tracking the current expansion state via the `allTreesExpanded` flag.
   *
   * @remarks Uses direct DOM manipulation to trigger tree node expansion/collapse.
   */
  toggleAllTrees(): void {
    if (this.treeTable) {
      // Access the internal tree nodes and toggle them
      const treeTableEl = this.treeTable.el.nativeElement
      const togglers = treeTableEl.querySelectorAll(
        this.allTreesExpanded
          ? '.p-treetable-toggler.p-treetable-toggler-expanded'
          : '.p-treetable-toggler:not(.p-treetable-toggler-expanded)'
      )

      togglers.forEach((toggler: HTMLElement) => toggler.click())
      this.allTreesExpanded = !this.allTreesExpanded
    }
  }

  nodeExpanded(): void {}
  nodeCollapsed(): void {}

  /**
   * Checks if a specific action exists for a given entity.
   *
   * @param entity The tree table entity node to check
   * @param actionName The name of the action to verify
   * @returns A boolean indicating whether the action exists and is defined for the entity
   */
  hasAction(entity: TreeTableEntityNode, actionName: string): boolean {
    return entity.actions.hasOwnProperty(actionName) && entity.actions[actionName] !== undefined
  }

  /**
   * Checks if a specific action is selected for a given entity.
   *
   * @param entity The tree table entity node to check
   * @param actionName The name of the action to verify
   * @returns A boolean indicating whether the action is selected for the entity
   */
  isActionSelected(entity: TreeTableEntityNode, actionName: string): boolean {
    return entity.actions[actionName] || false
  }

  /**
   * Determines if a specific entity is fully selected.
   *
   * @param entity The tree table entity node to check
   * @returns A boolean indicating whether the entity is completely selected
   */
  isEntitySelected(entity: TreeTableEntityNode): boolean {
    const state = this.getEntitySelectionState(entity)
    return state === true
  }

  /**
   * Determines if a specific entity has an indeterminate selection state.
   *
   * @param entity The tree table entity node to check
   * @returns A boolean indicating whether the entity is in an indeterminate (partially selected) state
   */
  isEntityIndeterminate(entity: TreeTableEntityNode): boolean {
    const state = this.getEntitySelectionState(entity)
    return state === null
  }

  /**
   * Checks if the given entity has any non-standard actions.
   *
   * @param entity The tree table entity node to check
   * @returns A boolean indicating whether the entity has non-standard actions
   */
  hasNonStandardActions(entity: TreeTableEntityNode): boolean {
    return entity.hasNonStandardActions || false
  }

  /**
   * Checks if a specific standard action exists and is defined for a given entity.
   *
   * @param entity The tree table entity node to check
   * @param actionName The name of the standard action to verify
   * @returns A boolean indicating whether the standard action exists and is defined for the entity
   */
  hasStandardAction(entity: TreeTableEntityNode, actionName: string): boolean {
    return (
      RightActionsEditorComponent.STANDARD_ACTIONS.includes(actionName as any) &&
      entity.actions.hasOwnProperty(actionName) &&
      entity.actions[actionName] !== undefined
    )
  }

  /**
   * Retrieves the dynamic actions for a given entity.
   *
   * @param entity The tree table entity node to extract dynamic actions from
   * @returns An array of dynamic action names that are not standard actions and are defined for the entity
   */
  getDynamicActions(entity: TreeTableEntityNode): string[] {
    return Object.keys(entity.actions).filter(
      (action) =>
        !RightActionsEditorComponent.STANDARD_ACTIONS.includes(action as any) && entity.actions[action] !== undefined
    )
  }

  /**
   * Retrieves the entities for the currently selected section from the tree table data.
   *
   * @param treeTableData The complete tree table data containing sections and entities
   * @returns An array of TreeTableEntityNode representing the entities for the current section
   *
   * @description
   * This method handles filtering and selection of entities based on the current section.
   * If no section is selected or no tree table data exists, it returns an empty array.
   * It supports filtering of entities and maintains the list of all and filtered entities.
   */
  getCurrentSectionEntities(treeTableData: any): TreeTableEntityNode[] {
    if (!treeTableData?.sections || !this.selectedSection) {
      return []
    }

    const currentSection = treeTableData.sections.find((section) => section.name === this.selectedSection.key)

    const entities = currentSection?.entities || []
    this.allEntities = entities

    return this.filteredEntities.length > 0 || this.filterValue ? this.filteredEntities : entities
  }

  /**
   * Handles input events for filtering entities in the tree table.
   *
   * @param event The input event triggered by the filter input
   * @description
   * Updates the filter value and filters the entities based on the input.
   * If the filter is empty, resets to show all entities.
   * Otherwise, applies a custom filter to find matching entities.
   */
  onFilterInput(event: any): void {
    this.filterValue = event.target.value

    if (!this.filterValue || this.filterValue.trim() === '') {
      this.filteredEntities = this.allEntities
    } else {
      this.filteredEntities = this.applyCustomFilter(this.allEntities, this.filterValue.toLowerCase())
    }
  }

  clearFilter(): void {
    this.filterValue = ''
    this.filteredEntities = this.allEntities
  }

  toggleDynamicActions(rowNode: any): void {
    rowNode.expanded = !rowNode.expanded
  }

  public getIconClass(action: string): string {
    const iconMap: Record<string, string> = {
      create: 'pi pi-plus',
      read: 'pi pi-eye',
      write: 'pi pi-pencil',
      delete: 'pi pi-trash',
      export: 'pi pi-file-excel',
      upload: 'pi pi-upload',
      download: 'pi pi-download'
    }
    return iconMap[action] || 'pi pi-cog'
  }

  private actionsWithSectionOnInit() {
    this.sections$
      .pipe(
        takeUntil(this.destroy$),
        tap((sections) => {
          // ✅ Save sections count for skeleton generation during next loading
          if (sections.length > 0) {
            this.lastSectionsCount = sections.length
            console.log('📊 Saved sections count for skeleton:', this.lastSectionsCount)
          }
        }),
        switchMap((sections) => {
          if (sections.length === 0) {
            return of([])
          }

          return this.treeTableData$.pipe(
            filter((treeData) => !!treeData && !!treeData.sections && treeData.sections.length > 0),
            take(1),
            tap(() => {
              this.selectAppropriateSection(sections)
              this.isInited = true
            })
          )
        })
      )
      .subscribe()
  }

  private tryToMakeRequestActionTreeFromBackend() {
    if (this.ownerId) {
      this.facade.requestActionTreeFromBackend(this.ownerId)
    }
  }

  private selectAppropriateSection(sections: SelectItem[]): void {
    const lastSelectedSectionKey: string = this.facade.getLastSelectedSectionKey()
    if (lastSelectedSectionKey) {
      const preferredSection: SelectItem = sections.find(
        (section: SelectItem) => section.value.key === lastSelectedSectionKey
      )

      if (preferredSection) {
        this.setSelectedSectionAndUpdate(preferredSection.value)
        return
      }
    }

    this.setSelectedSectionAndUpdate(sections[0].value)
  }

  private setSelectedSectionAndUpdate(sectionValue: any): void {
    this.selectedSection = sectionValue
    this.facade.selectSection(sectionValue.key)
    this.updateActionColumnsHeader(sectionValue.key)
    this.updateEntitiesForCurrentSection()
    this.applyCurrentFilterToNewSection()
  }

  public getSkeletonSectionsArray(): number[] {
    return new Array(this.lastSectionsCount)
  }

  private updateActionColumnsHeader(sectionKey: string): void {
    const newHeader = sectionKey === 'dto' ? 'dto-Typ/Feld' : 'Aktionen'
    this.actionColumns[0].header = newHeader
  }

  /**
   * Applies the current filter to the newly loaded section's entities.
   *
   * @description
   * Checks if a filter value exists and applies a custom filter to the current section's entities.
   * If no filter is active, resets the filtered entities to the full list of entities.
   * Uses RxJS operators to handle the filtering within a single subscription.
   */
  private applyCurrentFilterToNewSection(): void {
    this.treeTableData$
      .pipe(
        take(1),
        tap(() => {
          if (this.filterValue && this.filterValue.trim() !== '') {
            this.filteredEntities = this.applyCustomFilter(this.allEntities, this.filterValue.toLowerCase())
          } else {
            this.filteredEntities = this.allEntities
          }
        })
      )
      .subscribe()
  }

  /**
   * Updates the entities for the currently selected section.
   *
   * @description
   * Retrieves tree table data and finds the entities corresponding to the selected section.
   * Sets the `allEntities` property with the current section's entities.
   * Logs the number of entities found for the selected section.
   *
   * @internal
   */
  private updateEntitiesForCurrentSection(): void {
    this.treeTableData$.pipe(take(1)).subscribe((treeTableData) => {
      if (treeTableData?.sections && this.selectedSection) {
        const currentSection = treeTableData.sections.find((section) => section.name === this.selectedSection.key)

        this.allEntities = currentSection?.entities || []
      }
    })
  }

  /**
   * Filters tree table entities based on a search term.
   *
   * @description
   * Recursively filters entities by matching the search term against entity names
   * and their children's names. Returns a list of entities that either directly
   * match the search term or have matching children.
   *
   * @param entities The list of tree table entities to filter
   * @param searchTerm The lowercase search term to match against entity names
   * @returns An array of filtered tree table entities that match the search criteria
   * @internal
   */
  private applyCustomFilter(entities: TreeTableEntityNode[], searchTerm: string): TreeTableEntityNode[] {
    const filtered: TreeTableEntityNode[] = []

    entities.forEach((entity) => {
      const entityMatches = entity.name.toLowerCase().includes(searchTerm)
      const childMatches = this.hasMatchingChildren(entity, searchTerm)

      if (entityMatches || childMatches) {
        filtered.push(entity)
      }
    })

    return filtered
  }

  /**
   * Recursively checks if an entity has children that match the search term.
   *
   * @description
   * Traverses the entity's children tree to determine if any child's name
   * contains the given search term, either directly or through nested children.
   *
   * @param entity The tree table entity node to search within
   * @param searchTerm The lowercase search term to match against entity names
   * @returns A boolean indicating whether matching children were found
   * @internal
   */
  private hasMatchingChildren(entity: TreeTableEntityNode, searchTerm: string): boolean {
    if (!entity.children) return false

    return entity.children.some(
      (child) => child.name.toLowerCase().includes(searchTerm) || this.hasMatchingChildren(child, searchTerm)
    )
  }

  /**
   * Builds the full entity path for a given entity and action.
   *
   * @param entity The tree table entity node to build the path for
   * @param action The specific action associated with the entity
   * @returns A dot-separated string representing the full entity path, or an empty string for root-level dynamic actions
   * @internal
   */
  private buildEntityPathForAction(entity: TreeTableEntityNode, action: string): string {
    // For root-level dynamic actions (action name = entity name)
    if (this.isRootLevelDynamicAction(entity)) {
      return '' // example-path: "Administration.autoLoginActiveDevices"
    }

    const pathParts: string[] = []
    let current: TreeTableEntityNode | undefined = entity

    // Traverse up to build the full entity path
    while (current) {
      pathParts.unshift(current.name)
      current = current.parent
    }

    return pathParts.join('.')
  }

  /**
   * Recursively selects an entity and its children in the tree table.
   *
   * For root-level dynamic actions, it selects the action directly.
   * For standard entities, it selects all associated actions and recursively selects child entities.
   *
   * @param entity The tree table entity node to select
   * @internal
   */
  private selectEntityAndChildren(entity: TreeTableEntityNode): void {
    if (this.isRootLevelDynamicAction(entity)) {
      entity.selected = true
      const entityPath = this.buildEntityPathForAction(entity, entity.name)
      this.facade.toggleTreeTableAction(this.selectedSection?.key || '', entityPath, entity.name, true)
      return
    }

    entity.selected = true

    // Select ALL actions (standard + dynamic) for this entity
    Object.keys(entity.actions).forEach((action) => {
      if (entity.actions[action] !== undefined) {
        entity.actions[action] = true
      }
    })

    const entityPath: string = this.buildEntityPathForEntity(entity)
    this.facade.toggleTreeTableAction(this.selectedSection?.key || '', entityPath, null, true)

    if (entity.children) {
      entity.children.forEach((child: TreeTableEntityNode) => {
        this.toggleEntityAndChildrenLocally(child, true)
      })
    }
  }

  private toggleEntityAndChildrenLocally(entity: TreeTableEntityNode, isSelected: boolean): void {
    entity.selected = isSelected

    Object.keys(entity.actions).forEach((action: string) => {
      if (entity.actions[action] !== undefined) {
        entity.actions[action] = isSelected
      }
    })

    if (entity.children) {
      entity.children.forEach((child: TreeTableEntityNode) => {
        this.toggleEntityAndChildrenLocally(child, isSelected)
      })
    }
  }

  /**
   * Recursively unselects an entity and its children in the tree table.
   *
   * For root-level dynamic actions, it unselects the action directly.
   * For standard entities, it unselects all associated actions and recursively unselects child entities.
   *
   * @param entity The tree table entity node to unselect
   * @internal
   */
  private unselectEntityAndChildren(entity: TreeTableEntityNode): void {
    if (this.isRootLevelDynamicAction(entity)) {
      entity.selected = false
      const entityPath = this.buildEntityPathForAction(entity, entity.name)
      this.facade.toggleTreeTableAction(this.selectedSection?.key || '', entityPath, entity.name, false)
      return
    }

    entity.selected = false

    // Unselect ALL actions (standard + dynamic) for this entity
    Object.keys(entity.actions).forEach((action) => {
      if (entity.actions[action] !== undefined) {
        entity.actions[action] = false
      }
    })

    const entityPath = this.buildEntityPathForEntity(entity)
    this.facade.toggleTreeTableAction(this.selectedSection?.key || '', entityPath, null, false)

    if (entity.children) {
      entity.children.forEach((child) => {
        this.toggleEntityAndChildrenLocally(child, false)
      })
    }
  }

  private buildEntityPathForEntity(entity: TreeTableEntityNode): string {
    const pathParts: string[] = []
    let current: TreeTableEntityNode | undefined = entity

    while (current) {
      pathParts.unshift(current.name)
      current = current.parent
    }

    return pathParts.join('.')
  }

  /**
   * Checks if entity is a root-level dynamic action (like "autoLoginActiveDevices")
   * NOT an entity with dynamic actions (like "SystemSetting" with "modify")
   */
  private isRootLevelDynamicAction(entity: TreeTableEntityNode): boolean {
    const hasChildren = entity.children && entity.children.length > 0
    const hasStandardActions = RightActionsEditorComponent.STANDARD_ACTIONS.some(
      (action) => entity.actions[action] !== undefined
    )

    const definedActions = Object.keys(entity.actions).filter((action) => entity.actions[action] !== undefined)

    return !hasChildren && !hasStandardActions && definedActions.length === 1 && definedActions[0] === entity.name
  }

  /**
   * Determines the overall selection state of an entity in the tree table.
   *
   * @param entity The tree table entity node to evaluate
   * @returns
   * - `true` if the entity is fully selected
   * - `false` if the entity is completely unselected
   * - `null` if the entity has a partial/mixed selection state
   */
  private getEntitySelectionState(entity: TreeTableEntityNode): boolean | null {
    const ownActionState = this.calculateOwnActionState(entity)
    const childrenState = this.calculateChildrenState(entity)

    return this.combineSelectionStates(ownActionState, childrenState)
  }

  /**
   * Calculates the selection state of an entity's own actions.
   *
   * @returns An object indicating the selection status of the entity's actions:
   * - hasSelected: Whether any actions are selected
   * - hasUnselected: Whether any actions remain unselected
   */
  private calculateOwnActionState(entity: TreeTableEntityNode): { hasSelected: boolean; hasUnselected: boolean } {
    const definedActions = Object.keys(entity.actions).filter((key) => entity.actions[key] !== undefined)

    const selectedActions = definedActions.filter((action) => entity.actions[action] === true)

    return {
      hasSelected: selectedActions.length > 0,
      hasUnselected: selectedActions.length < definedActions.length
    }
  }

  /**
   * Calculates the selection state of an entity's children.
   *
   * @returns An object indicating the selection status of children:
   * - hasSelected: Whether any children are fully selected
   * - hasUnselected: Whether any children are unselected
   * - hasIndeterminate: Whether any children have a mixed/partial selection state
   */
  private calculateChildrenState(entity: TreeTableEntityNode): {
    hasSelected: boolean
    hasUnselected: boolean
    hasIndeterminate: boolean
  } {
    let hasSelectedChildren = false
    let hasUnselectedChildren = false
    let hasIndeterminateChildren = false

    if (entity.children) {
      entity.children.forEach((child) => {
        const childState = this.getEntitySelectionState(child)
        if (childState === true) {
          hasSelectedChildren = true
        } else if (childState === false) {
          hasUnselectedChildren = true
        } else if (childState === null) {
          hasIndeterminateChildren = true
        }
      })
    }

    return {
      hasSelected: hasSelectedChildren,
      hasUnselected: hasUnselectedChildren,
      hasIndeterminate: hasIndeterminateChildren
    }
  }

  /**
   * Determines the overall selection state of an entity based on its own state and its children's states.
   *
   * @param ownState The selection state of the entity's own actions
   * @param childrenState The selection state of the entity's children
   * @returns The combined selection state: true (fully selected), false (unselected), or null (indeterminate)
   */
  private combineSelectionStates(
    ownState: { hasSelected: boolean; hasUnselected: boolean },
    childrenState: { hasSelected: boolean; hasUnselected: boolean; hasIndeterminate: boolean }
  ): boolean | null {
    const hasAnySelected = ownState.hasSelected || childrenState.hasSelected
    const hasAnyUnselected = ownState.hasUnselected || childrenState.hasUnselected
    const hasAnyIndeterminate = childrenState.hasIndeterminate

    // If there's any indeterminate child OR mixed selection → indeterminate
    if (hasAnyIndeterminate || (hasAnySelected && hasAnyUnselected)) {
      return null // Indeterminate
    }

    // If everything is selected → selected
    if (hasAnySelected && !hasAnyUnselected) {
      return true
    }

    // Otherwise → unselected
    return false
  }

  private fixPanelWidthBeforeLoading(): void {
    if (this.sectionsPanel?.nativeElement) {
      const currentWidth: number = this.sectionsPanel.nativeElement.offsetWidth
      this.fixedPanelMinWidth = `${currentWidth}px`
    }
  }

  private releasePanelWidth(): void {
    this.fixedPanelMinWidth = null
  }

  private subscribeToLoadingState(): void {
    this.isLoading$.pipe(takeUntil(this.destroy$)).subscribe((isLoading: boolean) => {
      if (!isLoading && this.fixedPanelMinWidth) {
        this.releasePanelWidth()
      }
    })
  }
}
